import React, { useContext, useEffect, useMemo, useState } from 'react';
import '@stripe/stripe-js';
import { Box, Button, CardActionArea, Divider, Typography, ToggleButton, ToggleButtonGroup, IconButton } from "@mui/material";
import FormBox from './building_blocks/FormBox';
import SurfaceCard from './building_blocks/SurfaceCard';
import ProviderCard from './building_blocks/ProviderCard';
import CartQuantityStepper from './building_blocks/CartQuantityStepper';
import { currency } from './building_blocks/Currency';
import ProductPriceDisplay from './building_blocks/ProductPriceDisplay';
import PageHeader from './building_blocks/PageHeader';
import { apiBaseUrl } from '../utils/Api/Api';
import ProductPhotoPlaceholder from '../images/placeholder_product_image.png';
import { useNavigate } from 'react-router-dom';
import cardsPathTo from '../utils/Api/cardsPathTo';
import { providersPathTo } from '../utils/Api';
import ordersPathTo from '../utils/Api/ordersPathTo';
import campaignsPathTo from '../utils/Api/campaignsPathTo';
import visa from '../images/visa_card.png';
import master from '../images/master_card.png';
import SnackbarContext from '../contexts/SnackbarContext';
import { ShoppingCartProductsContext } from '../contexts/ShoppingCartProductsContext';
import { Elements } from '@stripe/react-stripe-js';
import NewCardDialog from './NewCardDialog';
import PaymentMethodsDialog from './PaymentMethodsDialog';
import stripeService from './stripeService';
import CheckoutPixPayment from './CheckoutPixPayment';
import { layoutCardRadius } from '../theme/colors';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';

// Re-enable when Stripe approves Pix on the platform account.
const PIX_CHECKOUT_ENABLED = false;

/* Padrão Informações */
const FieldInfo = ({ label, children }) => (
  /* Info Produto */
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>
    {/* Título*/}
    <Typography variant="sectionTitle">
      {label}
    </Typography>

    {/* Informação*/}
    {children}
  </Box>
);

/* Padrão Preços */
const Price = ({ label, children, isTotal = false }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', alignSelf: 'stretch' }}>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: '14px',
        lineHeight: '24px',
        fontWeight: 400,
        color: isTotal ? 'text.primary' : 'text.secondary',
      }}
    >
      {label}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'Nunito',
        fontSize: isTotal ? '18px' : '14px',
        lineHeight: '24px',
        fontWeight: isTotal ? 700 : 600,
        color: isTotal ? 'primary.main' : 'text.primary',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const ShoppingCartCheckout = () => {
  const stripePromise = stripeService.getStripe();
  const cachedProvider = useMemo(() => JSON.parse(localStorage.getItem('shoppingProvider') || 'null'), []);
  const [provider, setProvider] = useState(cachedProvider);
  const navigate = useNavigate()
  const currentUser = JSON.parse(localStorage.getItem('current_user'));
  const { openSnackbar } = useContext(SnackbarContext);
  const {
    shoppingCartProducts,
    totalAmount,
    totalQuantity,
    setShoppingCartProducts,
    setTotalAmount,
    setTotalQuantity,
  } = useContext(ShoppingCartProductsContext);
  const deliveryFee = 1000  // 10 reais a entrega
  const applicationFeeRate = 0.05;
  const [openNewCardDialog, setOpenNewCardDialog] = useState(false);
  const [openPaymentMethodsDialog, setOpenPaymentMethodsDialog] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [paymentKind, setPaymentKind] = useState('card');
  const [pixClientSecret, setPixClientSecret] = useState(null);
  const [pixInitLoading, setPixInitLoading] = useState(false);
  const [activeCampaign, setActiveCampaign] = useState(null);

  const amountAfterDiscount = (amount, discountType, discountValue) => {
    const value = Number(discountValue || 0);

    switch (discountType) {
      case 'percent':
        return Math.max(amount - Math.floor((amount * value) / 100), 0);
      case 'fixed_cents':
        return Math.max(amount - value, 0);
      case 'waive':
        return 0;
      default:
        return amount;
    }
  };

  const applicationFeeBase = Math.floor(totalAmount * applicationFeeRate);
  const deliveryFeeCharged = amountAfterDiscount(
    deliveryFee,
    activeCampaign?.delivery_fee_discount_type,
    activeCampaign?.delivery_fee_discount_value
  );
  const applicationFeeCharged = (() => {
    if (activeCampaign?.platform_fee_discount_type === 'percent') {
      const discountRate = Math.min(Number(activeCampaign.platform_fee_discount_value || 0) / 100, applicationFeeRate);
      return Math.floor(totalAmount * Math.max(applicationFeeRate - discountRate, 0));
    }

    return amountAfterDiscount(
      applicationFeeBase,
      activeCampaign?.platform_fee_discount_type,
      activeCampaign?.platform_fee_discount_value
    );
  })();
  const campaignDeliveryDiscountAmount = deliveryFee - deliveryFeeCharged;
  const campaignPlatformFeeDiscountAmount = applicationFeeBase - applicationFeeCharged;
  const totalDue = totalAmount + deliveryFeeCharged - campaignPlatformFeeDiscountAmount

  let selectedPaymentIndex = 0
  localStorage.getItem('payment_method') != null ? selectedPaymentIndex = localStorage.getItem('payment_method') : selectedPaymentIndex = 0

  useEffect(() => {
    if (cachedProvider?.id) {
      providersPathTo.get({ id: cachedProvider.id }).then((result) => {
        setProvider(result.data);
        localStorage.setItem('shoppingProvider', JSON.stringify(result.data));
      });
    }

    cardsPathTo.list().then(result => {
      setPaymentMethods(result.data);
      setSelectedPaymentMethod(result.data[0]); // selecting first payment method for now
    });
    campaignsPathTo.active()
      .then(result => setActiveCampaign(result.data.campaign))
      .catch(() => setActiveCampaign(null));
    document.title = 'Carrinho de compras - TudoPets.app';
  }, [cachedProvider?.id])

  const baseOrderPayload = () => ({
    provider_id: provider?.id,
    total_amount: totalAmount,
    total_quantity: totalQuantity,
    products: shoppingCartProducts,
    delivery_address: currentUser.default_address.logradouro + ", " + currentUser.default_address.numero,
  });

  const handleCheckoutCard = () => {
    ordersPathTo
      .save({
        ...baseOrderPayload(),
        payment_kind: 'card',
        payment_method_id: selectedPaymentMethod.id,
      })
      .then(() => {
        clearShoppingCart();
        openSnackbar('Pedido criado. O pagamento será processado quando a loja aceitar.', 'success');
        navigate('/checkout-success');
      })
      .catch((err) => {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível finalizar o pedido.';
        openSnackbar(msg, 'error');
      });
  };

  const handleStartPix = () => {
    setPixInitLoading(true);
    ordersPathTo
      .save({
        ...baseOrderPayload(),
        payment_kind: 'pix',
      })
      .then((result) => {
        const secret = result.data?.payment_intent_client_secret;
        if (!secret) {
          openSnackbar('Resposta do servidor sem client_secret do Pix.', 'error');
          return;
        }
        setPixClientSecret(secret);
        openSnackbar('Pedido criado. Conclua o Pix abaixo.', 'success');
      })
      .catch((err) => {
        const msg = err?.response?.data?.errors?.[0] || 'Não foi possível iniciar o Pix.';
        openSnackbar(msg, 'error');
      })
      .finally(() => setPixInitLoading(false));
  };

  const handlePixSuccess = () => {
    clearShoppingCart();
    openSnackbar('Pix concluído. A loja será avisada.', 'success');
    setPixClientSecret(null);
    navigate('/checkout-success');
  };

  const handleFinalizeOrder = () => {
    if (PIX_CHECKOUT_ENABLED && pixInitLoading) {
      return;
    }

    if (!shoppingCartProducts?.length || totalQuantity < 1) {
      openSnackbar('Adicione itens ao carrinho para finalizar o pedido.', 'info');
      return;
    }

    if (!currentUser?.default_address) {
      openSnackbar('Adicione um endereço de entrega para finalizar o pedido.', 'info');
      return;
    }

    if (PIX_CHECKOUT_ENABLED && paymentKind === 'pix') {
      handleStartPix();
      return;
    }

    if (!selectedPaymentMethod) {
      openSnackbar('Selecione ou adicione um método de pagamento para finalizar o pedido.', 'info');
      return;
    }

    handleCheckoutCard();
  };

  const clearShoppingCart = () => {
    localStorage.setItem("shoppingCartProducts", '[]');
    localStorage.setItem("totalAmount", '0');
    localStorage.setItem("totalQuantity", '0');
    setTotalAmount(0);
    setTotalQuantity(0);
    setShoppingCartProducts([]);
  }

  const clearShoppingCartAndGoToHome = () => {
    clearShoppingCart();
    openSnackbar("Carrinho esvaziado!", "success");
    navigate('/');
  }

  const handleCloseNewCardDialog = (paymentMethod) => {
    setOpenNewCardDialog(false);
    if (paymentMethod) {
      setPaymentMethods([...paymentMethods, paymentMethod]);
      setSelectedPaymentMethod(paymentMethod);
    }
  };

  const handleClosePaymentMethodsDialog = (paymentMethod = null) => {
    setOpenPaymentMethodsDialog(false);

    setSelectedPaymentMethod(paymentMethod);
  };

  const changeCartQuantity = (id, operation) => {
    const newShoppingCartProducts = [...shoppingCartProducts];
    const cartProduct = newShoppingCartProducts.find((product) => product.id === id);
    if (!cartProduct) {
      return;
    }

    if (operation === '+') {
      if (cartProduct.quantity === 0 || cartProduct.cartQuantity < cartProduct.quantity) {
        cartProduct.cartQuantity += 1;
      }
    } else {
      cartProduct.cartQuantity -= 1;
    }

    if (cartProduct.cartQuantity < 1) {
      const index = newShoppingCartProducts.findIndex((item) => item.id === id);
      newShoppingCartProducts.splice(index, 1);
    }

    setShoppingCartProducts(newShoppingCartProducts);
    localStorage.setItem('shoppingCartProducts', JSON.stringify(newShoppingCartProducts));
  };

  const removeItem = (id) => {
    const newShoppingCartProducts = shoppingCartProducts.filter((item) => item.id !== id);
    setShoppingCartProducts(newShoppingCartProducts);
    localStorage.setItem('shoppingCartProducts', JSON.stringify(newShoppingCartProducts));
  };

  const checkoutActionsSx = {
    display: 'flex',
    flexDirection: { xs: 'column', sm: 'row' },
    alignItems: 'stretch',
    gap: 1.5,
    alignSelf: 'stretch',
    width: '100%',
  };

  if (PIX_CHECKOUT_ENABLED && pixClientSecret) {
    return (
      <Box>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: pixClientSecret,
            appearance: { theme: 'stripe' },
            locale: 'pt-BR',
          }}
        >
          <Button onClick={() => setPixClientSecret(null)} sx={{ mb: 2, color: '#222' }}>
            ‹ Voltar ao carrinho
          </Button>
          <FormBox>
            <CheckoutPixPayment
              onSuccess={handlePixSuccess}
              onCancel={() => setPixClientSecret(null)}
            />
          </FormBox>
        </Elements>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Elements stripe={stripePromise}>
        <PageHeader title="Carrinho" onBack={() => navigate('/home')} />
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>

          {/* Loja */}
          {provider && (
            <ProviderCard
              provider={provider}
              showProducts={false}
              showBanner={true}
              categories
              onCardClick={() => navigate(`/provider/${provider.id}`)}
            />
          )}

          {/* Endereço de Entrega */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 1, alignSelf: 'stretch' }}>

            <Typography variant="sectionTitle">
              Entregar em:
            </Typography>

            <SurfaceCard sx={{ flexDirection: 'column', gap: 1 }}>
              {currentUser && currentUser.default_address &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' }, gap: 1, alignSelf: 'stretch' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <LocationOnIcon sx={{ color: 'primary.main' }} />
                    <Typography sx={{ fontFamily: 'Nunito', fontSize: '16px', fontWeight: 600, lineHeight: '20px', color: 'text.primary', overflowWrap: 'anywhere' }}>
                      {currentUser.default_address.logradouro}, {currentUser.default_address.numero}
                    </Typography>
                  </Box>
                  <IconButton
                    variant="subtle"
                    data-no-shadow="true"
                    aria-label="Editar endereço de entrega"
                    onClick={() => navigate('/addresses', { state: { returnTo: '/shopping-cart' } })}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>
              }
              {currentUser && !currentUser.default_address &&
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', alignSelf: 'stretch' }}>
                  <Button
                    onClick={() => navigate('/addresses/new?from=checkout', { state: { returnTo: '/shopping-cart' } })}>
                    Adicionar endereço de entrega
                  </Button>
                </Box>
              }
            </SurfaceCard>
          </Box>

          {/* Produtos */}
          <FieldInfo label="Produtos">
            <SurfaceCard sx={{ flexDirection: 'column', gap: 0, p: 0 }}>
              {shoppingCartProducts.map((product, index) => {
                const atMaxStock = product.quantity > 0 && product.cartQuantity >= product.quantity;

                return (
                  <React.Fragment key={product.id}>
                    {index > 0 && <Divider sx={{ alignSelf: 'stretch' }} />}
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: { xs: 'flex-start', sm: 'center' },
                        flexWrap: { xs: 'wrap', sm: 'nowrap' },
                        gap: { xs: 1.5, sm: 2 },
                        py: 1.5,
                        px: { xs: 1.5, sm: 3 },
                        alignSelf: 'stretch',
                        width: '100%',
                      }}
                    >
                      <Box
                        component="img"
                        alt={product.title}
                        src={product.item_photo ? `${apiBaseUrl}${product.item_photo}` : ProductPhotoPlaceholder}
                        sx={{
                          width: { xs: 64, sm: 80 },
                          height: { xs: 64, sm: 80 },
                          borderRadius: '16px',
                          objectFit: 'cover',
                          bgcolor: 'surface.input',
                          flexShrink: 0,
                        }}
                      />

                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.5, flex: 1, minWidth: 0 }}>
                        <Typography variant="storeName">{product.title}</Typography>
                        <ProductPriceDisplay product={product} compact sx={{ color: 'text.primary' }} />
                      </Box>

                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: { xs: 'space-between', sm: 'flex-end' },
                          gap: 1,
                          flexShrink: 0,
                          width: { xs: '100%', sm: 'auto' },
                          pl: { xs: '80px', sm: 0 },
                          boxSizing: 'border-box',
                        }}
                      >
                        <CartQuantityStepper
                          cartQuantity={product.cartQuantity}
                          onIncrement={() => changeCartQuantity(product.id, '+')}
                          onDecrement={() => changeCartQuantity(product.id, '-')}
                          disabledIncrement={atMaxStock}
                          stopPropagation={false}
                        />
                        <IconButton
                          variant="deleteItem"
                          data-no-shadow="true"
                          aria-label="Remover item"
                          onClick={() => removeItem(product.id)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  </React.Fragment>
                );
              })}

            </SurfaceCard>
            {provider && (
              <Box sx={{ py: 2, px: 0, alignSelf: 'stretch', width: '100%' }}>
                <Button
                  variant="outlinedPill"
                  fullWidth
                  onClick={() => navigate(`/provider/${provider.id}`)}
                >
                  Adicionar mais itens
                </Button>
              </Box>
            )}
          </FieldInfo>

          {/* Valores */}
          <FieldInfo label="Valores">
            <SurfaceCard sx={{ flexDirection: 'column' }}>
              {/*<Typography >Quantidade de itens: {totalQuantity}</Typography>
              <Typography >Taxa de entrega: {currency(deliveryFee)}</Typography>*/}

              {/* Subtotal */}
              <Price label="Subtotal:">
                {currency(totalAmount)}
              </Price>

              {/* Taxa de Entrega */}
              <Price label="Taxa de entrega:">
                {currency(deliveryFee)}
              </Price>

              {campaignDeliveryDiscountAmount > 0 && (
                <Price label="Desconto campanha na entrega:">
                  -{currency(campaignDeliveryDiscountAmount)}
                </Price>
              )}

              {campaignPlatformFeeDiscountAmount > 0 && (
                <Price label="Desconto campanha TudoPets.app:">
                  -{currency(campaignPlatformFeeDiscountAmount)}
                </Price>
              )}

              {activeCampaign && (
                <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Nunito' }}>
                  Campanha aplicada:{' '}
                  <Typography component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    {activeCampaign.name}
                  </Typography>
                </Typography>
              )}

              <Divider sx={{ width: '100%' }} />

              {/* Total */}
              <Price label="Total:" isTotal>
                {currency(totalDue)}
              </Price>
            </SurfaceCard>
          </FieldInfo>

          {/* Forma de Pagamento */}
          <FieldInfo label="Forma de pagamento">
            <SurfaceCard sx={{ flexDirection: 'column', gap: 2 }}>
              {PIX_CHECKOUT_ENABLED && (
                <>
                  <ToggleButtonGroup
                    color="primary"
                    value={paymentKind}
                    exclusive
                    fullWidth
                    onChange={(e, value) => value && setPaymentKind(value)}
                    sx={{
                      '& .MuiToggleButton-root': { fontFamily: 'Nunito' },
                    }}
                  >
                    <ToggleButton value="card">Cartão (crédito ou débito)</ToggleButton>
                    <ToggleButton value="pix">Pix</ToggleButton>
                  </ToggleButtonGroup>

                  {paymentKind === 'pix' && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontFamily: 'Nunito' }}>
                      Compras internacionais podem incorrer em IOF de 3,5% cobrado pelo banco, conforme regras da Stripe/Ebanx.
                    </Typography>
                  )}
                </>
              )}

              {(!PIX_CHECKOUT_ENABLED || paymentKind === 'card') && (
                <>
                  {paymentMethods && paymentMethods.length === 0 && (
                    <Button
                      variant="pillContained"
                      color="secondary"
                      onClick={() => setOpenNewCardDialog(true)}
                      sx={{ width: '100%' }}
                    >
                      Adicionar cartão
                    </Button>
                  )}
                  {selectedPaymentMethod && (
                    <CardActionArea
                      data-no-shadow="true"
                      aria-label="Alterar forma de pagamento"
                      onClick={() => setOpenPaymentMethodsDialog(true)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      flexWrap: { xs: 'wrap', sm: 'nowrap' },
                      gap: 2,
                      alignSelf: 'stretch',
                        p: 2,
                        borderRadius: layoutCardRadius,
                        bgcolor: 'surface.input',
                      }}
                    >
                      <Box sx={{ flexShrink: 0, lineHeight: 0 }}>
                        {selectedPaymentMethod.card.brand === 'visa' ? (
                          <img alt="Visa" src={visa} style={{ width: '56px', height: '36px', display: 'block' }} />
                        ) : (
                          <img alt="Mastercard" src={master} style={{ width: '56px', height: '36px', display: 'block' }} />
                        )}
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
                        <Typography
                          sx={{
                            fontFamily: 'Nunito',
                            fontSize: '16px',
                            fontWeight: 600,
                            lineHeight: '20px',
                            color: 'text.primary',
                            textTransform: 'capitalize',
                          }}
                        >
                          {selectedPaymentMethod.card.brand} ·{' '}
                          {selectedPaymentMethod.card.funding === 'debit' ? 'Débito' : 'Crédito'}
                        </Typography>
                        <Typography
                          sx={{
                            fontFamily: 'Nunito',
                            fontSize: '14px',
                            fontWeight: 400,
                            lineHeight: '21px',
                            color: 'text.secondary',
                          }}
                        >
                          **** {selectedPaymentMethod.card.last4}
                        </Typography>
                      </Box>

                      <KeyboardArrowRightIcon
                        sx={{ flexShrink: 0, color: 'text.secondary', fontSize: 28 }}
                        aria-hidden
                      />
                    </CardActionArea>
                  )}
                </>
              )}
            </SurfaceCard>
          </FieldInfo>

          {/* Finalizar e Limpar Carrinho */}
          <Box sx={checkoutActionsSx}>
          <Button
              variant="pillContained"
              color="secondary"
              onClick={() => clearShoppingCartAndGoToHome()}
              sx={{
                flex: { sm: 1 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              Limpar carrinho
            </Button>

            <Button
              variant="pillContained"
              onClick={handleFinalizeOrder}
              sx={{
                flex: { sm: 2 },
                width: { xs: '100%', sm: 'auto' },
              }}
            >
              {PIX_CHECKOUT_ENABLED && pixInitLoading
                ? 'Preparando Pix…'
                : PIX_CHECKOUT_ENABLED && paymentKind === 'pix'
                  ? 'Continuar com Pix'
                  : 'Finalizar pedido'}
            </Button>
          </Box>
        </Box>

        <NewCardDialog
          open={openNewCardDialog}
          onClose={handleCloseNewCardDialog}
        />
        <PaymentMethodsDialog open={openPaymentMethodsDialog} paymentMethods={paymentMethods} setPaymentMethods={setPaymentMethods} selectedPaymentIndex={selectedPaymentIndex} onClose={handleClosePaymentMethodsDialog} setOpenNewCardDialog={setOpenNewCardDialog} />
      </Elements>
    </Box>
  );
}

export default ShoppingCartCheckout
